import { Injectable, Type } from '@angular/core';
import type { PopoverHostComponent, PopoverImproved, PopoverImprovedOptions } from 'components/popover-host/popover-host';
import type { CfPopoverInterface } from 'components/popover-host/popover-interface';
import { isCompetingPopover, isSamePopover } from 'components/popover-host/popover-identity';

@Injectable()
export class PopoverController {
  popoverHost: PopoverHostComponent = undefined;

  constructor() { }

  public registerHost(host: PopoverHostComponent) {
    this.popoverHost = host;
  }

  isReady() {
    return this.popoverHost !== undefined
  }

  private async waitForReadyAsync(timeoutMs: number = 5000): Promise<boolean> {
    const startTime = Date.now();
    while (!this.isReady()) {
      if (Date.now() - startTime > timeoutMs) {
        console.error('PopoverController: popoverHost was never registered (timeout)');
        return false;
      }
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    return true;
  }

  async present(component: Type<CfPopoverInterface>, data: any, opts?: PopoverImprovedOptions & { returnInstance?: boolean }): Promise<any> {
    // wait for popoverHost to be registered (handles iOS Safari background recovery)
    if (!await this.waitForReadyAsync()) {
      return opts?.returnInstance ? { instance: null, promise: Promise.resolve({}) } : {};
    }

    // loop, because every wait below can let other popovers open or close
    for (;;) {
      let lastPopover = this.topPopover();
      if (lastPopover && lastPopover.animation) {
        await lastPopover.animation.finished;
        lastPopover = this.topPopover();
      }
      if (lastPopover && lastPopover.dismissPromise) {
        await lastPopover.dismissPromise;
        lastPopover = this.topPopover();
      }
      if (lastPopover && isSamePopover(lastPopover, component, opts)) {
        // duplicate popover of the same type, target and key — reuse the existing one
        // instead of dismissing it and returning empty (which breaks callers awaiting a result)
        return opts?.returnInstance
          ? { instance: lastPopover.componentRef.instance, promise: lastPopover.promise }
          : lastPopover.promise;
      }
      // if a different popover holds the same slot (for alerts: a different question is on
      // screen), wait until it has closed. Reusing it would hand this caller that popover's
      // answer; replacing it would dismiss a question nobody answered (df-93a444035272).
      const competing = this.popoverHost.popovers.find(p => isCompetingPopover(p, component, opts));
      if (!competing) {
        break;
      }
      await this.waitForPopoverClosedAsync(competing);
    }
    let popover = await this.popoverHost.createPopover(component, data, opts);

    // If returnInstance is true, return both instance and promise immediately
    if (opts?.returnInstance) {
      return {
        instance: popover.componentRef.instance,
        promise: popover.promise
      };
    }

    // Otherwise, wait for dismissal as before
    return popover.promise;
  }

  // the popover drawn on top of all others, or undefined when none is open
  private topPopover(): PopoverImproved | undefined {
    return this.popoverHost.popovers[this.popoverHost.popovers.length - 1];
  }

  // resolves once the popover has left the host. removePopover creates dismissPromise before it
  // resolves the popover's own promise, and resolves dismissPromise only after removing it.
  private async waitForPopoverClosedAsync(popover: PopoverImproved): Promise<void> {
    while (this.popoverHost.popovers.includes(popover)) {
      if (popover.dismissPromise) {
        await popover.dismissPromise;
      } else {
        await popover.promise;
        if (!popover.dismissPromise) {
          // the host was torn down while this popover was opening: its promise is answered but it
          // never leaves the stack, so wait on a timer instead of spinning on a settled promise
          await new Promise(resolve => setTimeout(resolve, 50));
        }
      }
    }
  }

  async dismiss(popover: CfPopoverInterface, data?: any) {
    if (!this.isReady()) {
      return;
    }
    return this.popoverHost.removePopover(popover, data);
  }
}
